import React from 'react';

interface UnauthorizedPageProps {
    appName: string;
    appLogo: string;
    darwinLoginUrl?: string;
}

// Inline SVG components for maximum compatibility across different environments
const ShieldXIcon = ({ className }: { className?: string }) => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className={className}
    >
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10" />
        <line x1="9.5" y1="9" x2="14.5" y2="14" />
        <line x1="14.5" y1="9" x2="9.5" y2="14" />
    </svg>
);

const ExternalLinkIcon = ({ className }: { className?: string }) => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className={className}
    >
        <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
        <polyline points="15 3 21 3 21 9" />
        <line x1="10" y1="14" x2="21" y2="3" />
    </svg>
);

export const UnauthorizedPage: React.FC<UnauthorizedPageProps> = ({ appName, appLogo, darwinLoginUrl }) => {
    const handleVisitGovern = () => {
        // Remove /login suffix if present and use the base URL
        const baseUrl = darwinLoginUrl?.replace(/\/login$/, '') || 'https://govern.darwingov.com';
        window.open(baseUrl, '_blank', 'noopener,noreferrer');
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4">
            <div className="max-w-md w-full space-y-8">
                {/* App Branding */}
                <div className="flex justify-center items-baseline mb-8">
                    <img
                        src={appLogo}
                        alt="Darwin logo"
                        className="h-6 w-auto"
                    />
                    <span className="text-lg font-medium text-gray-500" style={{ marginLeft: '1px', marginBottom: '1px' }}>
                        .{appName.toLowerCase().replace(/\s+/g, '')}
                    </span>
                </div>

                {/* Unauthorized Icon */}
                <div className="flex justify-center mb-6">
                    <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
                        <ShieldXIcon className="w-8 h-8 text-red-600" />
                    </div>
                </div>

                {/* Text Content */}
                <h2 className="mt-6 text-center text-2xl font-bold text-gray-900">
                    Access Required
                </h2>

                <p className="mt-4 text-center text-sm text-gray-600">
                    You don't have sufficient permissions to access {appName}.
                </p>

                {/* Action Button */}
                <div className="mt-8 space-y-4">
                    <button
                        onClick={handleVisitGovern}
                        style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '0.5rem',
                            width: '100%',
                            backgroundColor: '#0f172a',
                            color: '#ffffff',
                            fontWeight: '500',
                            padding: '0.75rem 1rem',
                            borderRadius: '0.5rem',
                            border: 'none',
                            cursor: 'pointer',
                            transition: 'background-color 0.2s',
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#1e293b'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#0f172a'}
                    >
                        <ExternalLinkIcon className="w-5 h-5" />
                        Visit Darwin Govern
                    </button>

                    <div className="text-center">
                        <p className="text-xs text-gray-500">
                            Contact your administrator to request access
                        </p>
                    </div>
                </div>

                {/* Footer */}
                <div className="text-center pt-4">
                    <p className="text-sm text-gray-600">
                        Powered by Darwin AI
                    </p>
                </div>
            </div>
        </div>
    );
};
